import
  React, {
  useState
}	from 'react';

import {
  useHistory,
  useLocation
} from 'react-router';

import {
  HContainer,
  HContainerVH,
  PillBar,
  Text,
  VFillContainer
} from '@vodacom/web-common';

import {
  BackButton,
  Modal,
  PrimaryButton
} from 'components';

import {
  HandleRender,
  withScreenViewTagging
} from 'hocs';

import {
  VoucherOrder,
  createGrandTotal,
  extractDays,
  formatDate,
  getStorageWithExpiry,
  handleUtagLinkEvent,
  isAdvanceEligible,
  isEqual,
  printCurrency
} from 'helpers';

import {
  CustomColors,
  ErrorKeys,
  Icons,
  PaymentOptions
} from '../../../../constants';

import {
  useCatalogue
} from '../hooks';

import {
  ItemSelector,
  PleaseNote,
  SecureCheckout
} from '../components';

import {
  VoucherAdvanceAbout
} from './VoucherAdvanceAbout';

const VoucherAdvanceCheckoutUI = () => {

  const location = useLocation();
  const history = useHistory();

  const [ state, setState ] = useState({
    showAboutVoucher: false
  });

  const [ paymentOption, setPaymentOption ] = useState(null);
  const [ advancePeriod, setAdvancePeriod ] = useState({ value: 'none', label: 'none' });

  const { vendorId, voucherId } = location.state;

  const catalogue = useCatalogue(vendorId, true, voucherId);
  const product = catalogue?.product;

  const eligibleForAdvance = isAdvanceEligible(catalogue.eligibility, catalogue.product?.retailPrice);
  const msisdnMasked = getStorageWithExpiry('msisdn', 'msisdnMasked');

  VoucherAdvanceCheckoutUI.tagViewData = {
    event_name: 'page_view,checkout_start',
    journey_step: 'cbu: connectu: voucher advance: which payment method',
    journey_name: 'cbu: connectu: voucher advance',
    journey_type : 'transaction',
    page_section: 'voucher advance',
    visitor_customer_segment: getStorageWithExpiry('msisdn', 'msisdnType', ''),
    visitor_type: 'consumer',
    product_category: catalogue.category,
    product_id: [ product?.name ],
    product_quantity: '1',
    product_price: product?.normal?.currency,
    visitor_id_asset_active: getStorageWithExpiry('msisdn', 'hashedMsisdn', '')
  };

  const getAdvanceTerms = () => {

    let termsList = [];

    for (const item of product?.terms?.duration) {

      if (!item?.offerId || !item?.typeId || !item?.valueType) {
        continue;
      }

      termsList.push({
        value: item.valueType,
        label: item.valueType
      });
    }

    return termsList;
  };

  const buildBadge = (normal, advance) => {

    const sum = Math.floor((normal - advance) / advance * 100);
    return sum > 0 ? `GET ${sum}% MORE` : null;
  };

  const PleaseNoteUI = () => {

    if (isEqual(advancePeriod.value, 'none') || !isEqual(paymentOption, PaymentOptions.Advance.value)) {

      return <></>;
    }

    const days = extractDays(advancePeriod.value);
    const paymentDate = new Date().toDateString();
    const formattedDate = formatDate(paymentDate, days);

    return (

      <PleaseNote
        message={`Please note: We will charge your account on the ${formattedDate} and no additional fees apply.`}/>
    );
  };

  const getPaymentOptions = () => {

    let optionsList = [];

    if ((product?.purchaseType?.advance || product?.purchaseType?.Advance) && getAdvanceTerms()?.length > 0 && eligibleForAdvance) {

      optionsList.push({
        'value': PaymentOptions.Advance.value,
        'label': PaymentOptions.Advance.label,
        'iconUrl': Icons.Advance
      });
    }

    optionsList.push({
      'value': PaymentOptions.Normal.value,
      'label': PaymentOptions.Normal.label,
      'iconUrl': Icons.PayNow,
      'badgeText': buildBadge(product?.normal?.currency, product?.retailPrice)
    });

    return optionsList;
  };

  const RenderContent = () => {

    return (

      <VFillContainer
        padding={'0 20px'}>

        <BackButton
          margin={'16px 0 0'}
          onClick={() => history.goBack()} />

        <Text
          fontSize={'26px'}
          fontWeight={'bold'}
          padding={'16px 0 30px'}
          color={CustomColors.Grey11}>

          {'Checkout'}
        </Text>

        <Text
          fontSize={'18px'}
          fontWeight={'bold'}
          color={CustomColors.Grey9}>

          {'Which payment option would you like?'}
        </Text>

        <ItemSelector
          padding={'16px 0'}
          inActiveBorder={`1px solid ${CustomColors.Grey5}`}
          boxShadow={'none'}
          onChange={value => {

            handleUtagLinkEvent({
              event_name: 'campaign_internal_click',
              link_id: `cbu: connectu: voucher advance: which payment method: ${value}`
            });

            setPaymentOption(value);
          }}
          items={getPaymentOptions()}
          defaultItem={paymentOption}/>

        <SecureCheckout
          border={`1px solid ${CustomColors.Grey5}`}/>

        { isEqual(paymentOption, PaymentOptions.Advance.value) &&

          <Text
            padding={'32px 0 0'}
            fontSize={'18px'}
            fontWeight={'bold'}
            color={CustomColors.Grey9}>

            {'Which payment option would you like?'}
          </Text>
        }

        <HContainer
          justifyContent={'space-between'}
          padding={'32px 0 16px'}>

          <Text
            fontSize={'16px'}
            fontWeight={'bold'}
            color={CustomColors.Grey9}>

            {product?.name}
          </Text>

          <Text
            fontSize={'16px'}
            fontWeight={'bold'}
            color={CustomColors.Grey16}>

            {printCurrency(product?.retailPrice)}
          </Text>

        </HContainer>

        <HContainer
          padding={isEqual(paymentOption, PaymentOptions.Advance.value) ? '16px 0 32px': '0 0 16px'}
          borderBottom={`1px solid ${CustomColors.Grey5}`}
          overflow={'auto'}>

          { isEqual(paymentOption, PaymentOptions.Advance.value) &&

            <PillBar
              defaultPill={advancePeriod.value}
              onPillClicked={value => {

                handleUtagLinkEvent({
                  event_name: 'campaign_internal_click',
                  link_id: `cbu: connectu: voucher advance: payment duration: ${value?.label}`
                });

                setAdvancePeriod(value);
              }}
              pills={getAdvanceTerms()}
              fontFamily={'Roboto'}
              fontSize={'14px'}
              fontWeight={'bold'}
              activeColor={CustomColors.Red1}
              inActiveColor={CustomColors.Grey15}
              activeFontColor={CustomColors.White}
              inActiveFontColor={CustomColors.Grey8}
              pillBarBackgroundColor={CustomColors.Transparent}
              minHeight={'40px'}
              pillBorder={'none'}
              pillMargin={'0 5px'}/>
          }
        </HContainer>

        <PleaseNoteUI/>

        <HContainerVH
          margin={'32px 0'}>

          <Text
            fontSize={'14px'}
            color={CustomColors.Grey16}
            whiteSpace={'pre-wrap'}>

            {'Grand total: '}
          </Text>

          <Text
            fontSize={'14px'}
            color={CustomColors.Grey16}
            fontWeight={'bold'}>

            {createGrandTotal(product?.retailPrice, product?.normal?.currency, paymentOption)}
          </Text>

        </HContainerVH>

        <PrimaryButton
          isEnabled={
            isEqual(paymentOption, PaymentOptions.Normal.value) ||
            (isEqual(paymentOption, PaymentOptions.Advance.value) && !isEqual(advancePeriod.value, 'none'))
          }
          text={'Proceed'}
          onClick={() => {

            setState(prevState => ({
              ...prevState,
              showAboutVoucher: true
            }));
          }}
          margin={'auto auto 34px'}/>

      </VFillContainer>
    );
  };

  return (

    <HandleRender
      isLoading={catalogue.isLoading}
      isError={catalogue.isError}
      error={{
        type: ErrorKeys.Technical
      }}>

      <>
        { state.showAboutVoucher &&

          <Modal
            backgroundColor={CustomColors.White}>

            <VoucherAdvanceAbout
              description={
                isEqual(paymentOption, PaymentOptions.Advance.value)
                 ? product?.advanceDescription
                 : product?.buyNowDescription
              }
              validity={product?.validity}
              onPrimaryClick={() => {

                const voucherOrder = new VoucherOrder(
                  catalogue.category,
                  catalogue.product,
                  msisdnMasked,
                  catalogue.vendor?.name,
                  catalogue.eligibility,
                  isAdvanceEligible(catalogue.eligibility, catalogue.product?.retailPrice),
                  paymentOption,
                  advancePeriod.label
                );

                history.push({
                  pathname: `${location.pathname}/summary`,
                  state: voucherOrder
                });
              }}
              onSecondaryClick={() => {

                setState(prevState => ({
                  ...prevState,
                  showAboutVoucher: false
                }));
              }}/>
          </Modal>
        }

        <RenderContent/>
      </>
    </HandleRender>
  );
};

VoucherAdvanceCheckoutUI.pageName = 'cbu: connectu: voucher advance: which payment method';

export const VoucherAdvanceCheckout = withScreenViewTagging(VoucherAdvanceCheckoutUI);